import { useEffect, useState } from "react"
import { Link } from "react-router"
import { Plus } from "lucide-react"
import type { Family } from "@mc/types"
import { listFamilies, createFamily } from "../api/families.js"
import { friendlyError } from "../api/errorMessages.js"
import { Spinner } from "../components/ui/Spinner.js"
import { Button } from "../components/ui/Button.js"
import { Input } from "../components/ui/Input.js"
import { useToast } from "../components/ui/Toast.js"

type PageState = { status: "loading" } | { status: "ready"; families: Family[] } | { status: "error"; message: string }

/** `/families` (FR section 8: "Lista de familias propias y donde es miembro" + creación). */
export function FamiliesPage() {
  const [state, setState] = useState<PageState>({ status: "loading" })
  const [showCreate, setShowCreate] = useState(false)
  const [name, setName] = useState("")
  const [description, setDescription] = useState("")
  const [creating, setCreating] = useState(false)
  const toast = useToast()

  useEffect(() => {
    load()
  }, [])

  function load() {
    setState({ status: "loading" })
    listFamilies()
      .then(({ families }) => setState({ status: "ready", families }))
      .catch((err) => setState({ status: "error", message: friendlyError(err).message }))
  }

  async function handleCreate() {
    setCreating(true)
    try {
      await createFamily({ name, description: description || null })
      toast.show("Familia creada.", "success")
      setName("")
      setDescription("")
      setShowCreate(false)
      load()
    } catch (err) {
      toast.show(friendlyError(err).message, "error")
    } finally {
      setCreating(false)
    }
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-6)" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "var(--space-3)" }}>
        <div>
          <h1 style={{ fontSize: "var(--text-3xl)" }}>Familias</h1>
          <p style={{ color: "var(--text-muted)" }}>Grupos para compartir acceso a servidores con roles propios.</p>
        </div>
        {!showCreate && (
          <Button onClick={() => setShowCreate(true)}>
            <Plus size={16} aria-hidden />
            Nueva familia
          </Button>
        )}
      </div>

      {showCreate && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "var(--space-3)",
            padding: "var(--space-4)",
            borderRadius: "var(--radius-md)",
            border: "1px solid var(--brand-primary)",
            background: "var(--bg-surface-raised)",
            maxWidth: 420,
          }}
        >
          <Input label="Nombre" value={name} onChange={(e) => setName(e.target.value)} />
          <Input label="Descripción" value={description} onChange={(e) => setDescription(e.target.value)} />
          <div style={{ display: "flex", gap: "var(--space-2)" }}>
            <Button loading={creating} disabled={!name} onClick={handleCreate}>
              Crear
            </Button>
            <Button variant="ghost" disabled={creating} onClick={() => setShowCreate(false)}>
              Cancelar
            </Button>
          </div>
        </div>
      )}

      {state.status === "loading" && (
        <div style={{ display: "grid", placeItems: "center", padding: "var(--space-12) 0" }}>
          <Spinner size="lg" />
        </div>
      )}

      {state.status === "error" && (
        <p role="alert" style={{ color: "var(--status-error)", fontSize: "var(--text-sm)" }}>
          {state.message}
        </p>
      )}

      {state.status === "ready" && state.families.length === 0 && (
        <p style={{ color: "var(--text-muted)" }}>Todavía no formás parte de ninguna familia.</p>
      )}

      {state.status === "ready" && state.families.length > 0 && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", gap: "var(--space-4)" }}>
          {state.families.map((family) => (
            <Link
              key={family.id}
              to={`/families/${family.id}`}
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "var(--space-2)",
                padding: "var(--space-4)",
                borderRadius: "var(--radius-md)",
                border: "1px solid var(--border-default)",
                background: "var(--bg-surface)",
                textDecoration: "none",
                color: "inherit",
                transition: "border-color var(--duration-small) var(--ease-out), transform var(--duration-micro) var(--ease-out)",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = "var(--brand-primary)"
                e.currentTarget.style.transform = "translateY(-2px)"
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "var(--border-default)"
                e.currentTarget.style.transform = "translateY(0)"
              }}
            >
              <strong style={{ fontSize: "var(--text-lg)" }}>{family.name}</strong>
              <p style={{ fontSize: "var(--text-sm)", color: "var(--text-muted)", minHeight: "1.4em" }}>
                {family.description ?? "Sin descripción."}
              </p>
            </Link>
          ))}
        </div>
      )}
    </div>
  )
}
